import { useState, useEffect, useRef, useCallback } from "react";
import { Barcode } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import LabelsLayout from "../componants/labels/LabelsLayout";
import LabelBreadcrumb from "../componants/labels/LabelBreadcrumb";
import DetailsItems from "../componants/labels/DetailsItems";
import { useDocument } from "../hooks/useDocument";

export default function CertificatEvigilanceDetails() {
  const { id } = useParams();
  const { docResponse } = useDocument("certificates", id);
  const { document: doc, isPending: loading, error } = docResponse;

  // State for current active section ID
  const [activeId, setActiveId] = useState("");
  const sectionRefs = useRef({});

  // Initialize activeId once doc is loaded
  useEffect(() => {
    if (doc?.sections?.length) {
      setActiveId(doc.sections[0].section.id);
    }
  }, [doc]);

  // Scroll & IntersectionObserver logic
  useEffect(() => {
    if (!doc?.sections) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -50% 0px" }
    );
    doc?.sections?.forEach(({ section }) => {
      const el = sectionRefs.current[section.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [doc]);

  const scrollTo = useCallback((id) => {
    setActiveId(id);
    sectionRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // Skeleton placeholders
  const renderNavSkeleton = () => (
    <nav className="hidden lg:block w-48 sticky top-10 p-4 border-r-4 border-blue-200">
      <ul className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i}>
            <div className="h-6 bg-gray-200 rounded animate-pulse" />
          </li>
        ))}
      </ul>
    </nav>
  );
  const renderContentSkeleton = () => (
    <div className="flex-1 px-4 lg:px-8 space-y-8">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i}>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
          {Array.from({ length: 4 }).map((__, j) => (
            <div
              key={j}
              className="h-4 bg-gray-200 rounded mb-2 animate-pulse"
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <LabelsLayout>
      <section className="container mx-auto">
        <LabelBreadcrumb
          title="e-vigilance Détail"
          link="/certificats/e-vigilance/recherche"
          id={doc ? `ID: ${doc.entityId}` : ""}
        />

        <div className="flex pt-4 px-6">
          {/* Sidebar */}
          {loading ? (
            renderNavSkeleton()
          ) : error ? null : (
            <div>
              <nav className="hidden lg:block w-48 sticky top-10 p-4 border-r-4 border-blue-200">
                <ul className="space-y-2">
                  {doc?.sections?.map(({ section }) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollTo(section.id)}
                        className={`block w-full text-left px-2 py-1 rounded transition-colors ${
                          activeId === section.id
                            ? "bg-blue-100 text-blue-800"
                            : "text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}

          {/* Main content */}
          {loading ? (
            renderContentSkeleton()
          ) : error ? (
            <div className="flex-1 px-4 lg:px-8 text-red-600">
              Erreur lors du chargement : {error}
            </div>
          ) : !doc?.sections.length ? (
            <div className="flex-1 px-4 lg:px-8 text-gray-600">
              Aucun contenu disponible.
            </div>
          ) : (
            <div className="flex-1 px-4 lg:px-8">
              {doc.sections.map(({ section }) => (
                <section
                  id={section.id}
                  key={section.id}
                  ref={(el) => (sectionRefs.current[section.id] = el)}
                  className="mb-12 scroll-mt-24"
                >
                  {
                    <DetailsItems
                      title={section.title}
                      data={section}
                      fields={section.fields}
                      information={section.information}
                      costumArrays={section.costumArrays}
                      costumFields={section.costumFields}
                    />
                  }
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </LabelsLayout>
  );
}
